// applicationDiscoveryService.js — client for /api/v1/applications
import { API_BASE_URL } from './config.js';
import fetchWrapper from '../components/utility/fetchWrapper.js';
import { readApiError } from '../utils/parseApiErrorMessage.js';

const BASE = `${API_BASE_URL}/api/v1/applications`;

function accountParams(clientId, awsAccountId) {
  const params = new URLSearchParams();
  params.append('client_id', clientId);
  params.append('aws_account_id', awsAccountId);
  return params.toString();
}

export const applicationDiscoveryService = {
  /**
   * Live tag-driven application discovery.
   */
  discoverApplications: async (clientId, awsAccountId, options = {}) => {
    const {
      regions = ['us-east-1'],
      applicationTagKey = 'ApplicationName',
      persist = true,
    } = options;

    const response = await fetchWrapper(`${BASE}/discover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        aws_account_id: awsAccountId,
        regions,
        application_tag_key: applicationTagKey,
        persist,
      }),
    });

    if (!response.ok) {
      throw await readApiError(response, 'Failed to discover applications');
    }

    return await response.json();
  },

  /**
   * Fast list from DynamoDB inventory.
   */
  listApplications: async (clientId, awsAccountId) => {
    const response = await fetchWrapper(
      `${BASE}/?${accountParams(clientId, awsAccountId)}`
    );

    if (!response.ok) {
      throw await readApiError(response, 'Failed to list applications');
    }

    return await response.json();
  },

  getApplication: async (clientId, awsAccountId, appSlug) => {
    if (!appSlug) throw new Error('appSlug is required');
    const response = await fetchWrapper(
      `${BASE}/${encodeURIComponent(appSlug)}?${accountParams(clientId, awsAccountId)}`
    );

    if (!response.ok) {
      throw await readApiError(response, 'Failed to get application details');
    }

    return await response.json();
  },

  /**
   * Dynamic Resource Explorer scan for taggable resources missing ApplicationName.
   */
  listUntaggedResources: async (clientId, awsAccountId, regions = ['us-east-1']) => {
    const params = new URLSearchParams();
    params.append('client_id', clientId);
    params.append('aws_account_id', awsAccountId);
    (Array.isArray(regions) ? regions : []).forEach((region) => {
      if (region) params.append('regions', region);
    });
    const response = await fetchWrapper(`${BASE}/untagged-resources?${params.toString()}`);

    if (!response.ok) {
      throw await readApiError(response, 'Failed to load untagged resources');
    }

    return await response.json();
  },

  /**
   * Tag AWS resources with ApplicationName and attach to an app (existing or create).
   */
  assignResources: async (clientId, awsAccountId, options = {}) => {
    const {
      appSlug,
      createApplication,
      resources = [],
      discoverAfter = true,
      regions,
      applicationTagKey = 'ApplicationName',
    } = options;

    const body = {
      client_id: clientId,
      aws_account_id: awsAccountId,
      resources: (resources || []).map((r) => ({
        arn: r.arn,
        region: r.region || undefined,
        resource_type: r.resource_type || undefined,
      })),
      application_tag_key: applicationTagKey,
      discover_after: discoverAfter !== false,
    };
    if (appSlug) {
      body.app_slug = appSlug;
    } else if (createApplication) {
      body.create_application = {
        name: createApplication.name,
        description: createApplication.description || undefined,
        owner: createApplication.owner || undefined,
        type: createApplication.type || undefined,
        environment: createApplication.environment || undefined,
      };
    }
    if (Array.isArray(regions) && regions.length) {
      body.regions = regions;
    }

    const response = await fetchWrapper(`${BASE}/assign-resources`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw await readApiError(response, 'Failed to assign resources to application');
    }

    return await response.json();
  },

  /**
   * Register an application (UX Add Application).
   */
  registerApplication: async (clientId, awsAccountId, fields = {}) => {
    const response = await fetchWrapper(`${BASE}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        aws_account_id: awsAccountId,
        name: fields.name,
        description: fields.description || undefined,
        owner: fields.owner || undefined,
        type: fields.type || undefined,
        environment: fields.environment || undefined,
      }),
    });

    if (!response.ok) {
      throw await readApiError(response, 'Failed to add application');
    }

    return await response.json();
  },

  updateApplication: async (clientId, awsAccountId, appSlug, fields = {}) => {
    if (!appSlug) throw new Error('appSlug is required');
    const response = await fetchWrapper(`${BASE}/${encodeURIComponent(appSlug)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        aws_account_id: awsAccountId,
        name: fields.name,
        description: fields.description || undefined,
        owner: fields.owner || undefined,
        type: fields.type || undefined,
        environment: fields.environment || undefined,
      }),
    });

    if (!response.ok) {
      throw await readApiError(response, 'Failed to update application');
    }

    return await response.json();
  },

  deleteApplication: async (clientId, awsAccountId, appSlug) => {
    if (!appSlug) throw new Error('appSlug is required');
    const response = await fetchWrapper(
      `${BASE}/${encodeURIComponent(appSlug)}?${accountParams(clientId, awsAccountId)}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      throw await readApiError(response, 'Failed to delete application');
    }

    return await response.json();
  },

  onboardApplication: async (clientId, awsAccountId, appSlug, scope = 'both', options = {}) => {
    const body = {
      client_id: clientId,
      aws_account_id: awsAccountId,
      app_slug: appSlug,
      scope,
    };
    if (Array.isArray(options.logSelectors) && options.logSelectors.length) {
      body.log_selectors = options.logSelectors;
    }
    if (Array.isArray(options.metricTargets) && options.metricTargets.length) {
      body.metric_targets = options.metricTargets.map((m) => {
        const entry = {
          namespace: m.namespace,
          dimensions: m.dimensions || {},
          region: m.region || 'us-east-1',
        };
        if (Array.isArray(m.metrics) && m.metrics.length) {
          entry.metrics = m.metrics;
        }
        return entry;
      });
    }
    const response = await fetchWrapper(`${BASE}/onboard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw await readApiError(response, 'Failed to onboard application');
    }

    return await response.json();
  },

  deboardApplication: async (clientId, awsAccountId, appSlug, scope = 'both', options = {}) => {
    const body = {
      client_id: clientId,
      aws_account_id: awsAccountId,
      app_slug: appSlug,
      scope,
    };
    if (Array.isArray(options.logSelectors) && options.logSelectors.length) {
      body.log_selectors = options.logSelectors;
    }
    if (Array.isArray(options.metricTargets) && options.metricTargets.length) {
      body.metric_targets = options.metricTargets.map((m) => {
        const entry = {
          namespace: m.namespace,
          dimensions: m.dimensions || {},
          region: m.region || 'us-east-1',
        };
        if (Array.isArray(m.metrics) && m.metrics.length) {
          entry.metrics = m.metrics;
        }
        return entry;
      });
    }
    const response = await fetchWrapper(`${BASE}/deboard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw await readApiError(response, 'Failed to deboard application');
    }

    return await response.json();
  },
};

export default applicationDiscoveryService;
